import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
    faCheck,
    faCircleCheck,
    faRotateRight,
    faArrowLeft,
    faCircleInfo,
    faBrain,
    faChevronDown,
    faChevronUp,
    faLightbulb
} from '@fortawesome/free-solid-svg-icons';
import DownloadBanner from '../components/DownloadBanner';
import './SurveyResults.css';

function SurveyResults({ result, survey, lang, onReset }) {
    const { t } = useTranslation();
    const [openFaqIndex, setOpenFaqIndex] = useState(null);

    const toggleFaq = (index) => {
        setOpenFaqIndex(prevIndex => (prevIndex === index ? null : index));
    };

    // Render formatted result description strings from analyzer
    const renderDescription = () => {
        if (!result || !result.resultKeys) return "";

        return result.resultKeys.map(item => {
            let text = survey?.translations?.results?.[item.key]?.[lang] 
                || survey?.translations?.results?.[item.key]?.['en'] 
                || "";
            if (item.params) {
                Object.keys(item.params).forEach(param => {
                    text = text.replace(`{{${param}}}`, item.params[param]);
                });
            }
            return text;
        }).join(' ');
    };

    // Extract dynamic metrics directly from the survey definition and result object
    const scoreVal = result?.details?.partAScore ?? result?.totalScore ?? 0;
    const maxScore = survey?.maxScore ?? (result?.details?.partAScore !== undefined ? 6 : 56);
    const threshold = survey?.threshold ?? (result?.details?.partAScore !== undefined ? 4 : 25);
    const scoreUnit = survey?.scoreUnitKey ? t(survey.scoreUnitKey) : t('Points');

    const isHighProbability = scoreVal >= threshold;
    const progressPercentage = Math.min(Math.max((scoreVal / maxScore) * 100, 5), 100);

    const dynamicSection = survey?.resultsSection;

    return (
        <div className="survey-wrapper-bs min-vh-100 d-flex flex-column bg-canvas-slate">
            <div className="container-fluid flex-grow-1 d-flex align-items-center justify-content-center px-3 py-5">
                <div className="w-100" style={{ maxWidth: '42rem' }}>
                    <div className="card survey-card-bs border-0 shadow-sm p-4 p-md-5 rounded-4 bg-white position-relative overflow-hidden">

                        <div className="d-flex flex-column w-100 align-items-center text-center">

                            {/* Verified Icon Header */}
                            <div className="position-relative mb-4 d-inline-block mx-auto">
                                <div className="rounded-circle bg-lavender d-flex align-items-center justify-content-center shadow-sm mx-auto" style={{ width: '80px', height: '80px' }}>
                                    <div className="rounded-circle bg-purple text-white d-flex align-items-center justify-content-center shadow-md" style={{ width: '56px', height: '56px' }}>
                                        <FontAwesomeIcon icon={faCheck} className="fs-4" />
                                    </div>
                                </div>
                                <div className="position-absolute bg-success text-white rounded-circle d-flex align-items-center justify-content-center shadow-sm border border-white" style={{ width: '24px', height: '24px', top: '4px', right: '4px' }}>
                                    <FontAwesomeIcon icon={faCircleCheck} style={{ fontSize: '12px' }} />
                                </div>
                            </div>

                            {/* Dynamic Title and Subhead */}
                            <h1 className="headline-title fw-bold text-dark mb-2">
                                {t('Your Screening Result')}
                            </h1>
                            <p className="text-secondary small d-flex align-items-center justify-content-center gap-2 flex-wrap mb-4">
                                <span>{survey?.translations?.name?.[lang] || survey?.translations?.name?.['en'] || survey?.title || t('Clinical Assessment')}</span>
                                <span className="dot-separator rounded-circle bg-secondary"></span>
                                <span className="text-success fw-semibold d-inline-flex align-items-center gap-1">
                                    <FontAwesomeIcon icon={faCircleCheck} /> {t('Completed')}
                                </span>
                            </p>

                            {/* Dynamic Score Status Pill */}
                            <div className={`badge-intro px-3 py-1.5 rounded-pill shadow-sm mb-4 d-inline-flex align-items-center gap-2 ${isHighProbability ? 'border-purple' : 'border-muted'}`}>
                                <span className={`pulse-dot rounded-circle ${isHighProbability ? 'bg-purple' : 'bg-secondary'}`}></span>
                                <span className="small fw-semibold text-purple-deep">
                                    {t('Score:')} <strong className="fw-bold text-purple">{scoreVal} / {maxScore}</strong> — {isHighProbability ? t('High Probability Threshold') : t('Low Probability Range')}
                                </span>
                            </div>

                            {/* Universal Score Visual Gauge */}
                            <div className="w-100 bg-light rounded-4 p-3 p-md-4 mb-4 text-start shadow-sm">
                                <div className="d-flex justify-content-between align-items-baseline mb-2">
                                    <span className="small fw-semibold text-secondary text-uppercase tracking-wide">{t('Metric Range & Threshold')}</span>
                                    <span className="small fw-bold text-purple">{scoreVal} {t('of')} {maxScore} {scoreUnit}</span>
                                </div>

                                <div className="progress rounded-pill bg-surface-container overflow-hidden d-flex p-1" style={{ height: '14px' }}>
                                    <div
                                        className={`h-100 rounded-pill ${isHighProbability ? 'progress-bar-gradient' : 'bg-secondary opacity-75'} shadow-inner transition-all`}
                                        style={{ width: `${progressPercentage}%` }}
                                    ></div>
                                </div>

                                <div className="position-relative w-100 mt-3 pt-1 d-flex justify-content-between text-muted extra-small">
                                    <div>
                                        <span className="d-block fw-semibold text-secondary">0 – {threshold - 1}</span>
                                        <span className="text-caption">{t('Low Probability')}</span>
                                    </div>

                                    <div
                                        className="position-absolute top-0 d-flex flex-column align-items-center translate-middle-x"
                                        style={{ left: `${Math.min(Math.max(progressPercentage, 10), 90)}%` }}
                                    >
                                        <div className={`score-marker-dot ${isHighProbability ? 'bg-purple-deep' : 'bg-secondary'} rounded-circle shadow-sm`}></div>
                                        <span className="fw-bold text-dark mt-1 bg-lavender px-2 py-0.5 rounded shadow-xs text-nowrap" style={{ fontSize: '10px' }}>
                                            {t('Your Score')} ({scoreVal})
                                        </span>
                                    </div>

                                    <div className="text-end">
                                        <span className="d-block fw-semibold text-purple">{threshold} – {maxScore}</span>
                                        <span className="text-caption">{t('Threshold Met')}</span>
                                    </div>
                                </div>

                                <div className="mt-4 pt-2 d-flex align-items-start gap-2 bg-white rounded-3 p-3 shadow-xs">
                                    <FontAwesomeIcon icon={faCircleInfo} className="text-purple fs-6 flex-shrink-0 mt-1" />
                                    <p className="extra-small text-secondary mb-0 leading-relaxed">
                                        {isHighProbability
                                            ? `${t('Clinical consensus indicates that scores of')} ${threshold} ${t('or greater indicate significant symptom burden warranting an evaluation by a licensed mental healthcare professional.')}`
                                            : t('Your results fall below the clinical threshold, indicating minimal immediate functional impact. Keep monitoring your personal well-being.')
                                        }
                                    </p>
                                </div>
                            </div>

                            {/* Clinical Interpretation Card */}
                            <div className="w-100 bg-lavender-light rounded-4 p-3 p-md-4 mb-4 text-start shadow-sm border border-accent">
                                <div className="d-flex align-items-center gap-2 mb-3">
                                    <div className="icon-wrapper-sm rounded-3 bg-purple-subtle d-flex align-items-center justify-content-center text-purple">
                                        <FontAwesomeIcon icon={faBrain} />
                                    </div>
                                    <h2 className="headline-sm fw-semibold text-dark mb-0 fs-6">
                                        {t('What Your Score Means')}
                                    </h2>
                                </div>
                                <p className="text-secondary small mb-0 leading-relaxed">
                                    {renderDescription()}
                                </p>
                            </div>

                            {/* Dynamic Psychoeducational Section & FAQs */}
                            {dynamicSection && (
                                <div className="w-100 mb-4 text-start">
                                    {/* Optional "Did You Know?" Card */}
                                    {dynamicSection.didYouKnow && (
                                        <div className="card bg-light border-0 rounded-4 p-3 p-md-4 mb-4 shadow-xs">
                                            <div className="d-flex align-items-center gap-2 mb-2 text-dark fw-bold">
                                                <FontAwesomeIcon icon={faLightbulb} className="text-warning fs-5" />
                                                <span>{t('Did You Know?')}</span>
                                            </div>
                                            <p className="text-secondary small mb-0 leading-relaxed">
                                                {dynamicSection.didYouKnow[lang] || dynamicSection.didYouKnow['en']}
                                            </p>
                                        </div>
                                    )}

                                    {/* Optional FAQ Accordion */}
                                    {dynamicSection.faqs && dynamicSection.faqs.length > 0 && (
                                        <div className="mb-4">
                                            <h3 className="h6 fw-bold text-dark text-center mb-3">{t('Frequently Asked Questions')}</h3>
                                            <div className="d-flex flex-column gap-2">
                                                {dynamicSection.faqs.map((item, index) => {
                                                    const isOpen = openFaqIndex === index;
                                                    return (
                                                        <div key={index} className="border rounded-3 overflow-hidden bg-white shadow-xs">
                                                            <button
                                                                type="button"
                                                                onClick={() => toggleFaq(index)}
                                                                className="w-100 btn btn-link text-decoration-none text-dark d-flex justify-content-between align-items-center p-3 text-start fw-semibold small shadow-none"
                                                            >
                                                                <span>{item.question[lang] || item.question['en']}</span>
                                                                <FontAwesomeIcon
                                                                    icon={isOpen ? faChevronUp : faChevronDown}
                                                                    className="text-muted ms-2"
                                                                    style={{ fontSize: '12px' }}
                                                                />
                                                            </button>
                                                            {isOpen && (
                                                                <div className="px-3 pb-3 border-top pt-2 text-secondary extra-small leading-relaxed bg-light">
                                                                    {item.answer[lang] || item.answer['en']}
                                                                </div>
                                                            )}
                                                        </div>
                                                    );
                                                })}
                                            </div>
                                        </div>
                                    )}

                                    {/* App Upsell CTA */}
                                    <DownloadBanner />
                                </div>
                            )}

                            {/* Centered Navigation Links */}
                            <div className="d-flex align-items-center justify-content-center gap-3 pt-2">
                                <button
                                    type="button"
                                    onClick={onReset}
                                    className="btn btn-link text-purple text-decoration-none fw-semibold small d-inline-flex align-items-center gap-1 p-0"
                                >
                                    <FontAwesomeIcon icon={faRotateRight} />
                                    <span>{t('Retake Test')}</span>
                                </button>

                                <span className="text-muted">•</span>

                                <Link
                                    to="/"
                                    className="btn btn-link text-secondary text-decoration-none fw-semibold small d-inline-flex align-items-center gap-1 p-0 hover-purple"
                                >
                                    <FontAwesomeIcon icon={faArrowLeft} />
                                    <span>{t('Back to Home')}</span>
                                </Link>
                            </div>

                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
}

export default SurveyResults;